import { Button } from "@package/subframe/components/Button";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { Loader } from "@package/subframe/components/Loader";
import { Select } from "@package/subframe/components/Select";
import {
  FeatherCheck,
  FeatherClock,
  FeatherLoader,
  FeatherPower,
  FeatherWifiOff,
} from "@subframe/core";
import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { useNursery } from "@/contexts/NurseryContext";
import type { LogEntryResponseDto } from "@/generated/model";
import { listRoomsQueryControllerList } from "@/generated/rooms/rooms";
import type {
  LogEntryWithSync,
  UseOfflineLogEntriesResult,
} from "@/hooks/use-offline-log-entries";
import { useSleepCheckTimer } from "@/hooks/use-sleep-check-timer";
import { offlineDb } from "@/lib/offline/db";

import { LogCheckConfirmationModal } from "./LogCheckConfirmationModal";

const SLEEP_CHECK_CONFIRMATION_TEXT = [
  "I confirm that I have checked each child using Look! Hear! Feel!",
  "All children were placed to sleep on their backs in their own bed or cot. They have a sheet and a blanket; they look comfortable, they are breathing, nothing is covering their face, and they are at a comfortable temperature.",
  "Note: If children have turned themselves onto their tummies to sleep, they can be left, provided all other checks are satisfied.",
];

const END_SESSION_CONFIRMATION_TEXT = [
  "This ends the current sleep session and stops the check timer.",
  "Logging another check starts a new session.",
];

type ConfirmationAction = "check" | "end";

const CONFIRMATIONS: Record<
  ConfirmationAction,
  { title: string; text: string[]; ctaLabel?: string }
> = {
  check: {
    title: "Confirm sleep check",
    text: SLEEP_CHECK_CONFIRMATION_TEXT,
  },
  end: {
    title: "End this sleep session?",
    text: END_SESSION_CONFIRMATION_TEXT,
    ctaLabel: "End session",
  },
};

interface NurseryRoom {
  id: string;
  name: string;
  isActive: boolean;
}

interface SleepCheckViewProps {
  logTypeId: string;
  /** Offline-first log entries, owned by the parent so the header count and
   *  this view share one source of truth (NAU-96 follow-up). */
  entries: UseOfflineLogEntriesResult["entries"];
  isLoading: boolean;
  isOffline: boolean;
  createEntry: UseOfflineLogEntriesResult["createEntry"];
}

export default function SleepCheckView({
  logTypeId,
  entries: allEntries,
  isLoading: entriesLoading,
  isOffline,
  createEntry,
}: SleepCheckViewProps) {
  const { selectedNurseryId } = useNursery();
  const nurseryId = selectedNurseryId ?? "";
  const [selectedRoomId, setSelectedRoomId] = useState<string>("");
  const [rooms, setRooms] = useState<NurseryRoom[]>([]);
  const [roomsLoading, setRoomsLoading] = useState(true);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Which action the confirmation is gating. Never reset on close: the dialog
  // animates out while still mounted, so blanking it would flash empty copy.
  const [pendingConfirmation, setPendingConfirmation] =
    useState<ConfirmationAction>("check");
  const [confirming, setConfirming] = useState(false);
  const debounceRef = useRef(false);

  // Fetch rooms with offline cache
  useEffect(() => {
    if (!nurseryId) return;

    (async () => {
      // Try cache first
      const cached = await offlineDb.logEntriesCache.get(`rooms-${nurseryId}`);
      if (cached) {
        setRooms(cached.entries as unknown as NurseryRoom[]);
        setRoomsLoading(false);
      }

      // Fetch from server if online
      if (navigator.onLine) {
        try {
          const res = await listRoomsQueryControllerList(nurseryId);
          const serverRooms = res.data as unknown as NurseryRoom[];
          setRooms(serverRooms);
          // Cache rooms (reusing logEntriesCache table for simplicity)
          await offlineDb.logEntriesCache.put({
            id: `rooms-${nurseryId}`,
            sessionId: nurseryId,
            entries: serverRooms as unknown as unknown[],
            cachedAt: Date.now(),
          });
        } catch {
          // Keep cached data
        }
      }
      setRoomsLoading(false);
    })();
  }, [nurseryId]);

  const activeRooms = useMemo(
    () => rooms?.filter((r) => r.isActive) ?? [],
    [rooms],
  );

  // Auto-select first room
  const effectiveRoomId =
    selectedRoomId || (activeRooms.length > 0 ? activeRooms[0]!.id : "");

  // Filter entries for selected room (only entries with data.type field)
  const roomEntries = useMemo(() => {
    if (!allEntries || !effectiveRoomId) return [];
    return allEntries.filter((e) => {
      const data = e.data as Record<string, unknown>;
      return (
        e.room?.id === effectiveRoomId &&
        (data.type === "check" || data.type === "session_end")
      );
    });
  }, [allEntries, effectiveRoomId]);

  // Derive session state for selected room
  const { isSessionActive, lastCheckEntry } = useMemo(() => {
    if (roomEntries.length === 0)
      return { isSessionActive: false, lastCheckEntry: null };

    // Entries come most recent first
    const lastEndIndex = roomEntries.findIndex((e) => {
      const data = e.data as Record<string, unknown>;
      return data.type === "session_end";
    });

    const lastCheckIndex = roomEntries.findIndex((e) => {
      const data = e.data as Record<string, unknown>;
      return data.type === "check";
    });

    // Active if there's a check and it's more recent than any session_end
    const active =
      lastCheckIndex !== -1 &&
      (lastEndIndex === -1 || lastCheckIndex < lastEndIndex);
    const lastCheck =
      lastCheckIndex !== -1 ? (roomEntries[lastCheckIndex] ?? null) : null;

    return { isSessionActive: active, lastCheckEntry: lastCheck };
  }, [roomEntries]);

  // Timer
  const lastCheckTime = lastCheckEntry
    ? new Date(lastCheckEntry.timestamp)
    : null;
  const timer = useSleepCheckTimer(lastCheckTime, isSessionActive);

  const openConfirm = useCallback(() => {
    if (!effectiveRoomId) return;
    if (debounceRef.current) return;
    debounceRef.current = true;
    setTimeout(() => {
      debounceRef.current = false;
    }, 2000);
    setPendingConfirmation("check");
    setConfirmOpen(true);
  }, [effectiveRoomId]);

  // No debounce guard here, unlike openConfirm: the modal's overlay swallows a
  // double-tap, and confirming unmounts the trigger.
  const openEndConfirm = useCallback(() => {
    if (!effectiveRoomId) return;
    setPendingConfirmation("end");
    setConfirmOpen(true);
  }, [effectiveRoomId]);

  const confirmAndLog = useCallback(async () => {
    if (!effectiveRoomId) return;
    setConfirming(true);
    try {
      await createEntry({
        logTypeId,
        data: { type: "check" },
        roomId: effectiveRoomId,
      });
      setConfirmOpen(false);
    } finally {
      setConfirming(false);
    }
  }, [effectiveRoomId, logTypeId, createEntry]);

  const confirmAndEnd = useCallback(async () => {
    if (!effectiveRoomId) return;
    setConfirming(true);
    try {
      await createEntry({
        logTypeId,
        data: { type: "session_end" },
        roomId: effectiveRoomId,
      });
      setConfirmOpen(false);
    } finally {
      setConfirming(false);
    }
  }, [effectiveRoomId, logTypeId, createEntry]);

  // Selected room name
  const selectedRoomName =
    activeRooms.find((r) => r.id === effectiveRoomId)?.name ?? "";

  if (roomsLoading) {
    return (
      <div className="flex w-full items-center justify-center py-12">
        <Loader size="large" />
      </div>
    );
  }

  if (activeRooms.length === 0) {
    return (
      <div className="flex w-full flex-col items-center justify-center gap-2 py-12">
        <span className="text-body font-body text-subtext-color">
          No rooms configured. Contact your manager.
        </span>
      </div>
    );
  }

  return (
    <div className="flex w-full flex-col gap-6">
      {/* Offline banner */}
      {isOffline && (
        <div className="flex w-full items-center gap-3 rounded-sm bg-warning-100 px-4 py-3">
          <IconWithBackground
            variant="warning"
            size="medium"
            icon={<FeatherWifiOff />}
            square={true}
          />
          <span className="text-body font-body text-warning-800">
            You&apos;re offline. Checks will sync when reconnected.
          </span>
        </div>
      )}

      {/* Room selector + end session. The end action lives here, above the
          unbounded checks list, so it stays reachable as checks accumulate. */}
      <div className="flex w-full flex-wrap items-end gap-3">
        <div className="min-w-[12rem] flex-1">
          <Select
            label="Select room"
            value={effectiveRoomId}
            onValueChange={setSelectedRoomId}
          >
            {activeRooms.map((room) => (
              <Select.Item key={room.id} value={room.id}>
                {room.name}
              </Select.Item>
            ))}
          </Select>
        </div>
        {isSessionActive && (
          <Button
            icon={<FeatherPower />}
            onClick={openEndConfirm}
            variant="neutral-secondary"
            size="large"
            className="shrink-0"
          >
            End sleep session
          </Button>
        )}
      </div>

      {/* Timer card */}
      <div
        className={`flex w-full flex-col items-center gap-3 rounded-xl border p-6 ${
          timer?.isOverdue
            ? "border-red-200 bg-red-50"
            : "border-neutral-200 bg-neutral-50"
        }`}
      >
        <span className="text-caption font-caption text-subtext-color">
          {isSessionActive
            ? timer?.isOverdue
              ? "Check overdue"
              : "Next check due in"
            : "No active session"}
        </span>
        <div className="flex items-center gap-2">
          <FeatherClock
            className={`shrink-0 text-[2.5rem] leading-none ${timer?.isOverdue ? "text-red-500" : "text-subtext-color"}`}
          />
          <span
            className={`text-[2.5rem] font-semibold tracking-tight tabular-nums ${
              timer?.isOverdue
                ? "text-red-600"
                : isSessionActive
                  ? "text-default-font"
                  : "text-subtext-color"
            }`}
          >
            {timer ? timer.display : "--:--"}
          </span>
        </div>
        <Button
          icon={<FeatherCheck />}
          onClick={openConfirm}
          variant="brand-primary"
          size="large"
          disabled={!effectiveRoomId}
          className="w-full max-w-xs"
        >
          Log check
        </Button>
      </div>

      {/* Today's checks */}
      <div className="flex w-full flex-col gap-3">
        <h2 className="text-body-bold font-body-bold text-default-font">
          Today&apos;s checks
        </h2>

        {entriesLoading ? (
          <div className="flex w-full items-center justify-center py-8">
            <Loader size="medium" />
          </div>
        ) : roomEntries.length === 0 ? (
          <div className="flex w-full flex-col items-center justify-center gap-1 py-8">
            <span className="text-body font-body text-subtext-color">
              No checks recorded yet
            </span>
          </div>
        ) : (
          <div className="flex w-full flex-col gap-2">
            {roomEntries.map((entry, index) => {
              const prevEntry = roomEntries[index + 1];
              const data = entry.data as Record<string, unknown>;
              const prevData = prevEntry?.data as
                | Record<string, unknown>
                | undefined;
              let wasOverdue = false;
              if (
                data.type === "check" &&
                prevEntry &&
                prevData?.type === "check"
              ) {
                const gap =
                  new Date(entry.timestamp).getTime() -
                  new Date(prevEntry.timestamp).getTime();
                wasOverdue = gap > 10 * 60 * 1000;
              }
              return (
                <CheckEntryRow
                  key={entry.id}
                  entry={entry}
                  roomName={selectedRoomName}
                  wasOverdue={wasOverdue}
                />
              );
            })}
          </div>
        )}
      </div>

      <LogCheckConfirmationModal
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        title={CONFIRMATIONS[pendingConfirmation].title}
        confirmationText={CONFIRMATIONS[pendingConfirmation].text}
        primaryCtaLabel={CONFIRMATIONS[pendingConfirmation].ctaLabel}
        onConfirm={
          pendingConfirmation === "end" ? confirmAndEnd : confirmAndLog
        }
        isSubmitting={confirming}
      />
    </div>
  );
}

function CheckEntryRow({
  entry,
  roomName,
  wasOverdue,
}: {
  entry: LogEntryResponseDto | LogEntryWithSync;
  roomName: string;
  wasOverdue?: boolean;
}) {
  const data = entry.data as Record<string, unknown>;
  const isEnd = data.type === "session_end";
  const isPending =
    "localSyncState" in entry && entry.localSyncState === "pending";
  const recordedByName =
    [entry.recordedBy.firstName, entry.recordedBy.lastName]
      .filter(Boolean)
      .join(" ") || entry.recordedBy.email;

  return (
    <div
      className={`flex w-full items-center gap-3 rounded-lg border px-4 py-3 ${
        wasOverdue
          ? "border-red-200 bg-red-50"
          : "border-neutral-200 bg-neutral-50"
      }`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
          isEnd ? "bg-neutral-200" : wasOverdue ? "bg-red-100" : "bg-green-100"
        }`}
      >
        {isEnd ? (
          <FeatherPower className="h-4 w-4 text-subtext-color" />
        ) : (
          <FeatherCheck
            className={`h-4 w-4 ${wasOverdue ? "text-red-600" : "text-green-600"}`}
          />
        )}
      </div>
      <div className="flex grow flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <span className="text-body-bold font-body-bold text-default-font">
            {format(new Date(entry.timestamp), "HH:mm")}
          </span>
          {isPending && (
            <FeatherLoader className="h-3 w-3 animate-spin text-subtext-color" />
          )}
        </div>
        <span className="text-caption font-caption text-subtext-color">
          {isEnd ? "Session ended" : roomName}
        </span>
      </div>
      <span className="text-caption font-caption text-subtext-color">
        {recordedByName}
      </span>
    </div>
  );
}
